import type { SupabaseClient } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase/client'
import type { CheckInEntry } from '@/lib/offline/offlineDb'

export type CheckInMode = 'single' | 'multiple'
export type CheckInMethod = 'qr' | 'manual'
export type CheckInResultKind = 'valid' | 'already_checked_in' | 'invalid' | 'wrong_event'

export type CheckInResult = Readonly<{
  result: CheckInResultKind
  attendeeName: string | null
  reference: string | null
  /** When this check-in was recorded (valid results). */
  recordedAt: string | null
  /** When the attendee was first checked in, for already checked in. */
  previousCheckedInAt: string | null
  checkInCount: number | null
  /** Saved on this device and waiting to sync, rather than confirmed by the server. */
  offline?: boolean
  /** A walk-up just registered and checked in at the Box Office. */
  boxOffice?: boolean
}>

export type CheckInEvent = Readonly<{ id: string; name: string; locator: string; code: string; mode: CheckInMode; eventDate: string; ticketed: boolean; timezone?: string | null }>

export type CheckInRequest = Readonly<{
  clientCheckInId: string
  eventId: string
  method: CheckInMethod
  ticketToken?: string | undefined
  registrationId?: string | undefined
  checkedInAt?: string | undefined
  origin?: 'online' | 'offline'
}>

/** `refused`: the server understood and won't accept it (for example, the event doesn't use tickets). */
export type CheckInServiceErrorKind = 'offline' | 'refused' | 'unexpected'

const NO_CHECK_IN = 'You don’t have the Check-in permission any more. Ask someone who can manage permissions.'

/** A refusal for a missing permission. 42501 also means "authentication required", which a retry fixes. */
function lacksCheckIn(error: unknown): boolean {
  const { code, message } = (error ?? {}) as { code?: string; message?: string }
  return code === '42501' && /permission/i.test(message ?? '')
}

export class CheckInServiceError extends Error {
  constructor(message: string, public readonly kind: CheckInServiceErrorKind = 'unexpected') {
    super(message)
    this.name = 'CheckInServiceError'
  }
}

type DatasetRow = {
  registration_id: string
  client_registration_id?: string | null
  first_name: string
  last_name: string | null
  phone_last4: string | null
  ticket_id: string | null
  ticket_reference: string | null
  ticket_token_digest: string | null
  ticket_short_digest?: string | null
  check_in_count: number
  first_checked_in_at: string | null
  last_checked_in_at: string | null
  last_checked_in_by?: string | null
}
type ResultRow = { result: CheckInResultKind; attendee_name: string | null; reference: string | null; recorded_at: string | null; previous_checked_in_at: string | null; check_in_count: number | null }
type EventRow = { id: string; name: string; public_locator: string; ticket_code: string; check_in_mode: CheckInMode; event_date: string; ticketing_mode: string; timezone?: string | null }

export interface CheckInService {
  events(): Promise<CheckInEvent[]>
  event(eventId: string): Promise<CheckInEvent>
  dataset(eventId: string): Promise<CheckInEntry[]>
  checkIn(request: CheckInRequest): Promise<CheckInResult>
  /** Searches the server's current registrations for the event. */
  search(eventId: string, query: string): Promise<CheckInEntry[]>
}

const mapEntry = (row: DatasetRow): CheckInEntry => ({
  registrationId: row.registration_id,
  ...(row.client_registration_id ? { clientRegistrationId: row.client_registration_id } : {}),
  firstName: row.first_name,
  lastName: row.last_name,
  phoneLast4: row.phone_last4,
  ticketId: row.ticket_id,
  ticketReference: row.ticket_reference,
  ticketTokenDigest: row.ticket_token_digest,
  ticketShortDigest: row.ticket_short_digest ?? null,
  checkInCount: row.check_in_count,
  firstCheckedInAt: row.first_checked_in_at,
  lastCheckedInAt: row.last_checked_in_at,
  lastCheckedInBy: row.last_checked_in_by ?? null,
})

const mapEvent = (row: EventRow): CheckInEvent => ({ id: row.id, name: row.name, locator: row.public_locator, code: row.ticket_code, mode: row.check_in_mode, eventDate: row.event_date, ticketed: row.ticketing_mode === 'ticketed', timezone: row.timezone ?? null })

function requireOnline() {
  if (!navigator.onLine) throw new CheckInServiceError('You appear to be offline.', 'offline')
}

/** A failure that means the request may never have arrived, so it can be saved on the device instead. */
export function isConnectionFailure(error: unknown) {
  return (error instanceof CheckInServiceError && error.kind === 'offline') || error instanceof TypeError
}

export function createCheckInService(client: SupabaseClient = supabase): CheckInService {
  const eventColumns = 'id, name, public_locator, ticket_code, check_in_mode, event_date, ticketing_mode, timezone'
  return {
    async events() {
      requireOnline()
      const result = await client.from('events').select(eventColumns).order('event_date', { ascending: false }) as unknown as { data: EventRow[] | null; error: unknown }
      if (result.error) throw new CheckInServiceError('Events could not be loaded. Check your connection and try again.')
      return (result.data ?? []).map(mapEvent)
    },
    async event(eventId) {
      requireOnline()
      const result = await client.from('events').select(eventColumns).eq('id', eventId).single() as unknown as { data: EventRow | null; error: unknown }
      if (result.error || !result.data) throw new CheckInServiceError('This event could not be loaded.')
      return mapEvent(result.data)
    },
    async dataset(eventId) {
      requireOnline()
      const result = await client.rpc('get_check_in_dataset', { event_id: eventId }) as unknown as { data: DatasetRow[] | null; error: unknown }
      if (lacksCheckIn(result.error)) throw new CheckInServiceError(NO_CHECK_IN, 'refused')
      if (result.error) throw new CheckInServiceError('The check-in list could not be downloaded. Check your connection and try again.')
      return (result.data ?? []).map(mapEntry)
    },
    async checkIn(request) {
      requireOnline()
      const result = await client.rpc('check_in', {
        client_check_in_id: request.clientCheckInId,
        event_id: request.eventId,
        method: request.method,
        ticket_token: request.ticketToken ?? null,
        registration_id: request.registrationId ?? null,
        checked_in_at: request.checkedInAt ?? null,
        origin: request.origin ?? 'online',
      }) as unknown as { data: ResultRow[] | null; error: { code?: string; message?: string } | null }
      if (lacksCheckIn(result.error)) {
        throw new CheckInServiceError(`${NO_CHECK_IN} This check-in is kept on this phone.`, 'refused')
      }
      if (result.error?.code === '22023') {
        throw new CheckInServiceError(result.error.message?.includes('ticketed') ? 'Check-in is only for events that use tickets.' : (result.error.message ?? 'This check-in could not be recorded.'), 'refused')
      }
      if (result.error) throw new CheckInServiceError('The check-in could not be recorded. Please try again.')
      const row = result.data?.[0]
      if (!row) throw new CheckInServiceError('The check-in could not be recorded. Please try again.')
      return { result: row.result, attendeeName: row.attendee_name, reference: row.reference, recordedAt: row.recorded_at, previousCheckedInAt: row.previous_checked_in_at, checkInCount: row.check_in_count }
    },
    async search(eventId, query) {
      requireOnline()
      const result = await client.rpc('search_check_in', { event_id: eventId, query }) as unknown as { data: DatasetRow[] | null; error: unknown }
      if (lacksCheckIn(result.error)) throw new CheckInServiceError(NO_CHECK_IN, 'refused')
      if (result.error) throw new CheckInServiceError('Search isn’t available right now.')
      return (result.data ?? []).map(mapEntry)
    },
  }
}

export const checkInService = createCheckInService()
